import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CaretDown as ChevronDown } from '@phosphor-icons/react';
import { usePortalRoot } from '../../context/PortalContext';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';
import './ui-transforms.css';
import './dropdown.css';

/**
 * Dropdown component - custom single selection dropdown
 * Used for sort and other single-choice filters with custom styling
 */
const Dropdown = ({
  id,
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  className = '',
  triggerClassName = '',
  dropdownClassName = '',
  children, // For custom trigger (like icon button)
  align = 'left', // 'left' | 'right'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const portalRoot = usePortalRoot();

  // Dynamic dropdown positioning via shared hook
  const handleCloseAll = useCallback(() => setIsOpen(false), []);
  const dropdownPosition = useDropdownPosition(isOpen, containerRef, dropdownRef, {
    defaultMaxHeight: 300,
    onCloseAll: handleCloseAll,
  });

  // Close when another dropdown opens (coordination via CustomEvent)
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('dropdown-open', { detail: { id } }));
    }
  }, [isOpen, id]);

  useEffect(() => {
    const handleOtherOpen = (e) => {
      if (e.detail.id !== id) {
        setIsOpen(false);
      }
    };
    window.addEventListener('dropdown-open', handleOtherOpen);
    return () => window.removeEventListener('dropdown-open', handleOtherOpen);
  }, [id]);

  // Close on click outside
  // Используем composedPath() для корректной работы в Shadow DOM
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!containerRef.current && !dropdownRef.current) return;

      // composedPath() показывает полный путь события включая Shadow DOM
      // event.target может быть retargeted на shadow host, но composedPath() работает корректно
      const path = event.composedPath ? event.composedPath() : [event.target];
      const isInsideContainer = containerRef.current && path.includes(containerRef.current);
      const isInsideDropdown = dropdownRef.current && path.includes(dropdownRef.current);

      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`dropdown ${className}`.trim()}>
      {/* Custom trigger or default button */}
      {children ? (
        <div
          onClick={() => setIsOpen(!isOpen)}
          onTouchEnd={(e) => {
            e.preventDefault();
            setIsOpen(!isOpen);
          }}
          className="dropdown__custom-trigger ui-touch-action"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setIsOpen(!isOpen)}
        >
          {/* pointer-events: none чтобы клики проходили к родительскому div, а не к вложенному button */}
          <span className="dropdown__inert">
            {children}
          </span>
        </div>
      ) : (
        <button
          id={id}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          onTouchEnd={(e) => {
            e.preventDefault();
            setIsOpen(!isOpen);
          }}
          className={`dropdown__trigger ${triggerClassName}`.trim()}
        >
          <span className={`dropdown__value${selectedOption ? '' : ' dropdown__value--placeholder'}`}>
            {selectedOption?.label || placeholder}
          </span>
          <ChevronDown className={`dropdown__chevron ui-chevron${isOpen ? ' ui-chevron-open' : ''}`} />
        </button>
      )}

      {/* Dropdown - rendered via Portal to avoid overflow issues */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          data-portal-dropdown="true"
          className={`dropdown__menu${dropdownClassName ? '' : ' dropdown__menu--auto-width'}`}
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            ...(align === 'right' ? { right: dropdownPosition.right } : { left: dropdownPosition.left }),
            zIndex: 9999,
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={`dropdown__item${value === option.value ? ' dropdown__item--selected' : ''}`}
            >
              {option.label}
            </button>
          ))}
        </div>,
        portalRoot
      )}
    </div>
  );
};

export { Dropdown };
export default Dropdown;
